import type { TechniqueStatus } from '@/sections/attacks/attackPaths/types';

/**
 * Color constants for Attack Paths and MITRE ATT&CK visualizations.
 *
 * techniqueColors - Used in Attack Paths graph for node styling
 * defenseLevelColors - Used in MITRE ATT&CK graph for defense level display
 * markerColors - Used for edge arrow markers in Attack Paths graph
 *
 * lineHex/markerHex - Resolved hex values for PNG export (Tailwind classes aren't
 * processed when DOM is cloned, so we need explicit hex values)
 */

/**
 * Technique status colors for Attack Paths graph nodes and edges.
 * Keys are lowercase status values.
 *
 * Hex values are based on CSS variables in index.css:
 * - chariot-stroke-low: Green-500 (#22C55E light / #34D399 dark)
 * - chariot-stroke: Gray-200 (#E5E7EB light / #374151 dark)
 * - chariot-stroke-critical: Red-800 (#991B1B light) / Red-600 (#DC2626 dark)
 * - chariot-stroke-high: Orange-500 (#F97316 light) / Orange-400 (#FB923C dark)
 * - chariot-stroke-medium: Yellow-500 (#EAB308 light) / Yellow-400 (#FACC15 dark)
 */
export const techniqueColors: Record<
  string,
  {
    bg: string;
    border: string;
    text: string;
    /** Background class for status dot indicators */
    dot: string;
    line: string;
    marker: string;
    lineHex: { light: string; dark: string };
    markerHex: { light: string; dark: string };
  }
> = {
  untested: {
    bg: 'bg-chariot-background-primary',
    border: 'border-chariot-stroke',
    text: 'text-chariot-text-secondary',
    dot: 'bg-chariot-stroke',
    line: '[&>path]:stroke-chariot-stroke',
    marker: 'text-chariot-stroke',
    lineHex: { light: '#D1D5D9', dark: '#535B61' }, // chariot-stroke light / dark
    markerHex: { light: '#D1D5D9', dark: '#535B61' },
  },
  tested: {
    bg: 'bg-chariot-background-primary',
    border: 'border-chariot-stroke-red',
    text: 'text-chariot-text-secondary',
    dot: 'bg-chariot-text-primary',
    line: '[&>path]:stroke-chariot-text-primary',
    marker: 'text-chariot-text-primary',
    lineHex: { light: '#0D0D0D', dark: '#FFFFFF' }, // chariot-text-primary light / dark
    markerHex: { light: '#0D0D0D', dark: '#FFFFFF' },
  },
  undetected: {
    bg: 'bg-chariot-background-primary',
    border: 'border-chariot-stroke-blue',
    text: 'text-chariot-text-secondary',
    dot: 'bg-chariot-stroke-blue',
    line: '[&>path]:stroke-chariot-stroke-blue',
    marker: 'text-chariot-stroke-blue',
    lineHex: { light: '#2563EB', dark: '#3B82F6' }, // Blue-600 / Blue-500
    markerHex: { light: '#2563EB', dark: '#3B82F6' },
  },
  logged: {
    bg: 'bg-chariot-background-primary',
    border: 'border-chariot-stroke-medium',
    text: 'text-chariot-text-secondary',
    dot: 'bg-chariot-stroke-medium',
    line: '[&>path]:stroke-chariot-stroke-medium',
    marker: 'text-chariot-stroke-medium',
    lineHex: { light: '#EAB308', dark: '#FACC15' }, // Yellow-500 / Yellow-400
    markerHex: { light: '#EAB308', dark: '#FACC15' },
  },
  alerted: {
    bg: 'bg-chariot-background-primary',
    border: 'border-chariot-stroke-cyan',
    text: 'text-chariot-text-secondary',
    dot: 'bg-chariot-stroke-cyan',
    line: '[&>path]:stroke-chariot-stroke-cyan',
    marker: 'text-chariot-stroke-cyan',
    lineHex: { light: '#06B6D4', dark: '#22D3EE' }, // Cyan-500 / Cyan-400
    markerHex: { light: '#06B6D4', dark: '#22D3EE' },
  },
  detected: {
    bg: 'bg-chariot-background-primary',
    border: 'border-chariot-stroke-magenta',
    text: 'text-chariot-text-secondary',
    dot: 'bg-chariot-stroke-magenta',
    line: '[&>path]:stroke-chariot-stroke-magenta',
    marker: 'text-chariot-stroke-magenta',
    lineHex: { light: '#D946EF', dark: '#E879F9' }, // Fuchsia-500 / Fuchsia-400
    markerHex: { light: '#D946EF', dark: '#E879F9' },
  },
  prevented: {
    bg: 'bg-chariot-background-primary',
    border: 'border-chariot-stroke-light-green',
    text: 'text-chariot-text-secondary',
    dot: 'bg-chariot-stroke-light-green',
    line: '[&>path]:stroke-chariot-stroke-light-green',
    marker: 'text-chariot-stroke-light-green',
    lineHex: { light: '#22C55E', dark: '#4ADE80' }, // Green-500 / Green-400
    markerHex: { light: '#22C55E', dark: '#4ADE80' },
  },
};

/**
 * Human-readable labels for technique statuses.
 */
export const statusLabels: Record<TechniqueStatus, string> = {
  tested: 'Tested',
  untested: 'Untested',
  logged: 'Logged',
  alerted: 'Alerted',
  undetected: 'Undetected',
  detected: 'Detected',
  prevented: 'Prevented',
};

/**
 * Edge arrow marker colors for Attack Paths graph.
 * Extends techniqueColors with a default empty string key.
 */
export const markerColors: Record<
  string,
  { marker: string; markerHex?: { light: string; dark: string } }
> = {
  '': {
    marker: 'text-chariot-text-secondary',
    markerHex: { light: '#6B7280', dark: '#D1D5DB' }, // Gray-500 / Gray-300
  },
  ...techniqueColors,
};

/**
 * Defense level colors for MITRE ATT&CK visualization.
 * Keys are capitalized defense level names.
 *
 * Color semantics for security heatmap:
 * - Tested (blue): Validated coverage - we confirmed this works
 * - Untested (gray dashed): Coverage gap - unknown status
 * - Undetected (red): Critical gap - tested but NOT detected
 * - Logged (orange): Partial visibility - logged but not alerted
 * - Alerted (yellow): Alert fired - security team notified
 * - Detected (blue): Detected - identified the threat
 * - Responded (cyan): Response taken - action initiated
 * - Prevented (green): Blocked - attack stopped
 */
export const defenseLevelColors = {
  // Tested = Indigo/purple (validated coverage - distinct from Detected blue)
  Tested: {
    background: 'hover:bg-indigo-700 bg-indigo-600',
    text: 'text-white',
    lineClassName: 'text-indigo-400 fill-indigo-400',
    dotClassName: 'stroke-indigo-400',
    stroke: '#818cf8', // indigo-400
  },
  // Untested = Distinct gray (coverage gap - we don't know)
  Untested: {
    background: 'hover:bg-slate-600 bg-slate-700',
    text: 'text-slate-300',
    lineClassName: 'text-slate-400 fill-slate-400',
    dotClassName: 'stroke-slate-400',
    stroke: '#94a3b8', // slate-400
  },
  // Undetected = RED (critical gap - tested but not detected)
  Undetected: {
    background: 'hover:bg-red-600 bg-red-500/80',
    text: 'text-white',
    lineClassName: 'text-red-500 fill-red-500',
    dotClassName: 'stroke-red-500',
    stroke: '#ef4444', // red-500
  },
  // Logged = ORANGE (partial visibility - logged but not alerted)
  Logged: {
    background: 'hover:bg-orange-600 bg-orange-500/80',
    text: 'text-white',
    lineClassName: 'text-orange-500 fill-orange-500',
    dotClassName: 'stroke-orange-500',
    stroke: '#f97316', // orange-500
  },
  // Alerted = YELLOW (alert fired - security team notified)
  Alerted: {
    background: 'hover:bg-yellow-600 bg-yellow-500/80',
    text: 'text-black',
    lineClassName: 'text-yellow-500 fill-yellow-500',
    dotClassName: 'stroke-yellow-500',
    stroke: '#eab308', // yellow-500
  },
  // Detected = BLUE (detected - identified the threat)
  Detected: {
    background: 'hover:bg-blue-600 bg-blue-500/80',
    text: 'text-white',
    lineClassName: 'text-blue-500 fill-blue-500',
    dotClassName: 'stroke-blue-500',
    stroke: '#3b82f6', // blue-500
  },
  // Responded = CYAN/TEAL (response taken - action initiated)
  Responded: {
    background: 'hover:bg-cyan-600 bg-cyan-500/80',
    text: 'text-white',
    lineClassName: 'text-cyan-500 fill-cyan-500',
    dotClassName: 'stroke-cyan-500',
    stroke: '#06b6d4', // cyan-500
  },
  // Prevented = GREEN (blocked - attack stopped)
  Prevented: {
    background: 'hover:bg-green-600 bg-green-500/80',
    text: 'text-white',
    lineClassName: 'text-green-500 fill-green-500',
    dotClassName: 'stroke-green-500',
    stroke: '#22c55e', // green-500
  },
};
